import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { ArrowLeft, Save, Upload } from 'lucide-react';

const CompanySettings = () => {
  const navigate = useNavigate();
  const { profile, refreshProfile, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    website_url: '',
    industry: '',
    size: '',
    stage: '',
    description: '',
    contact_name: '',
    contact_email: '',
    contact_role: ''
  });

  useEffect(() => {
    if (profile) {
      setFormData({
        name: profile.name || '',
        website_url: profile.website_url || '',
        industry: profile.industry || '',
        size: profile.size || '',
        stage: profile.stage || '',
        description: profile.description || '',
        contact_name: profile.contact_name || '',
        contact_email: profile.contact_email || '',
        contact_role: profile.contact_role || ''
      });
    }
  }, [profile]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.put(`${API}/company/profile`, formData);
      await refreshProfile();
      toast.success('Company profile updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="company-settings-page">
      <Header />

      <div className="container-custom py-12 max-w-3xl">
        <button
          onClick={() => navigate('/company/dashboard')}
          className="flex items-center text-sm text-slate-500 hover:text-slate-700 mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Dashboard
        </button>

        <div className="mb-8">
          <h1 className="heading-2 mb-2">Company Settings</h1>
          <p className="text-slate-600">Update your company information</p>
        </div>

        <form onSubmit={handleSubmit}>
          <Card className="p-8 mb-6" data-testid="company-info-card">
            <h2 className="heading-4 mb-6">Company Information</h2>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Company Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="website">Website URL</Label>
                <Input
                  id="website"
                  type="url"
                  value={formData.website_url}
                  onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                  data-testid="website-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="industry">Industry</Label>
                <Input
                  id="industry"
                  value={formData.industry}
                  onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                  data-testid="industry-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Company Size</Label>
                  <Select value={formData.size} onValueChange={(v) => setFormData({ ...formData, size: v })}>
                    <SelectTrigger data-testid="size-select">
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1-10">1-10 employees</SelectItem>
                      <SelectItem value="11-50">11-50 employees</SelectItem>
                      <SelectItem value="51-200">51-200 employees</SelectItem>
                      <SelectItem value="201-500">201-500 employees</SelectItem>
                      <SelectItem value="500+">500+ employees</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Stage</Label>
                  <Select value={formData.stage} onValueChange={(v) => setFormData({ ...formData, stage: v })}>
                    <SelectTrigger data-testid="stage-select">
                      <SelectValue placeholder="Select stage" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="seed">Seed</SelectItem>
                      <SelectItem value="series_a">Series A</SelectItem>
                      <SelectItem value="series_b">Series B</SelectItem>
                      <SelectItem value="series_c">Series C</SelectItem>
                      <SelectItem value="growth">Growth</SelectItem>
                      <SelectItem value="public">Public</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="min-h-[120px]"
                  data-testid="description-input"
                />
              </div>
            </div>
          </Card>

          <Card className="p-8 mb-6" data-testid="contact-info-card">
            <h2 className="heading-4 mb-6">Primary Contact</h2>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="contactName">Name</Label>
                <Input
                  id="contactName"
                  value={formData.contact_name}
                  onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                  data-testid="contact-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contactEmail">Email</Label>
                <Input
                  id="contactEmail"
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                  data-testid="contact-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contactRole">Role</Label>
                <Input
                  id="contactRole"
                  value={formData.contact_role}
                  onChange={(e) => setFormData({ ...formData, contact_role: e.target.value })}
                  data-testid="contact-role-input"
                />
              </div>
            </div>
          </Card>

          <Card className="p-8 mb-6" data-testid="account-info-card">
            <h2 className="heading-4 mb-6">Account</h2>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Account Email</Label>
                <Input value={user?.email || ''} disabled className="bg-slate-50" />
                <p className="text-xs text-slate-500">Contact support to change your email</p>
              </div>

              <div className="space-y-2">
                <Label>Company Status</Label>
                <div>
                  <span className={profile?.status === 'approved' ? 'badge-success' : 'badge-warning'}>
                    {profile?.status || 'pending'}
                  </span>
                </div>
              </div>
            </div>
          </Card>

          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" onClick={() => navigate('/company/dashboard')}>
              Cancel
            </Button>
            <Button type="submit" className="btn-accent" disabled={loading} data-testid="save-settings-btn">
              <Save className="h-4 w-4 mr-2" />
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>

      <Footer />
    </div>
  );
};

export default CompanySettings;
